import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { FileText, Eye, Download, ChevronDown, ChevronUp } from "lucide-react";
import { PORTFOLIO, PORTFOLIO_FILTERS, WORK_SAMPLES, REPORT_FILTERS } from "../../data/content";
import { track } from "../../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Portfolio = () => {
  const [filter, setFilter] = useState("all");
  const [showAllReports, setShowAllReports] = useState(false);
  const [reportFilter, setReportFilter] = useState("All");
  const [downloads, setDownloads] = useState({});
  const INITIAL_REPORTS = 6;

  useEffect(() => {
    axios.get(`${API}/analytics/summary`)
      .then((r) => {
        const map = {};
        (r.data?.report_downloads || []).forEach((d) => { map[d.title] = d.count; });
        setDownloads(map);
      })
      .catch(() => {});
  }, []);

  const orderedReports = useMemo(() => {
    const filtered = WORK_SAMPLES.filter((r) => reportFilter === "All" || r.tags.includes(reportFilter));
    // Surface the most-downloaded work first; stable for ties / fresh dashboards.
    return [...filtered].sort((a, b) => (downloads[b.title] || 0) - (downloads[a.title] || 0));
  }, [reportFilter, downloads]);

  const visibleReports = showAllReports ? orderedReports : orderedReports.slice(0, INITIAL_REPORTS);
  const selectReportFilter = (f) => { setReportFilter(f); setShowAllReports(false); };

  return (
    <section className="section section--alt" id="portfolio" aria-labelledby="portfolio-heading">
      <div className="container">
        <div className="section-header">
          <div className="section-label">Work Samples</div>
          <h2 className="section-heading" id="portfolio-heading">Selected <em>Engagements</em></h2>
          <p className="section-sub">
            A curated selection of evaluation, research, and MEL assignments demonstrating methodological
            depth, geographic range, and decision-use impact.
          </p>
        </div>

        <div className="reports-block" data-testid="featured-reports">
          <h3 className="reports-title">Featured Reports — <em>Read the Work</em></h3>
          <p className="reports-sub">
            {WORK_SAMPLES.length} public-facing deliverables — independent evaluations, country case studies,
            and learning briefs. Filter by sector or region; most-downloaded surface first.
          </p>

          <div className="reports-filters" role="group" aria-label="Filter reports by sector or region">
            {REPORT_FILTERS.map((f) => (
              <button key={f} className={`filter-btn ${reportFilter === f ? "active" : ""}`}
                      data-testid={`report-filter-${f.replace(/\s/g, "-").toLowerCase()}`}
                      aria-pressed={reportFilter === f} onClick={() => selectReportFilter(f)}>
                {f}
              </button>
            ))}
          </div>

          <div className="reports-grid">
            {visibleReports.map((r, i) => (
              <article className="report-card" key={r.file} data-testid={`report-card-${i}`}>
                <div className="report-icon" aria-hidden="true"><FileText size={22} /></div>
                <div className="report-body">
                  <div className="report-type">{r.type}</div>
                  <h4 className="report-title">{r.title}</h4>
                  <div className="report-org">{r.org}</div>
                  <p className="report-desc">{r.desc}</p>
                  <div className="report-meta">{r.place} · {r.year} · PDF · {r.size}</div>
                  <div className="report-actions">
                    <a className="report-btn report-btn--view" href={r.file} target="_blank" rel="noopener noreferrer"
                       onClick={() => track("sample_download", r.title)} data-testid={`report-view-${i}`}>
                      <Eye size={14} /> View
                    </a>
                    <a className="report-btn report-btn--dl" href={r.file} download
                       onClick={() => track("sample_download", r.title)} data-testid={`report-download-${i}`}>
                      <Download size={14} /> Download
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
          {visibleReports.length === 0 && (
            <p className="reports-empty" data-testid="reports-empty">No reports in this category yet.</p>
          )}
          {orderedReports.length > INITIAL_REPORTS && (
            <div className="reports-more">
              <button className="reports-more-btn" data-testid="reports-toggle"
                      onClick={() => setShowAllReports((v) => !v)}>
                {showAllReports ? "Show fewer reports" : `View all ${orderedReports.length} reports`}
                {showAllReports ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>
            </div>
          )}
        </div>

        <div className="portfolio-filters" role="group" aria-label="Filter portfolio by type">
          {PORTFOLIO_FILTERS.map(([key, label]) => (
            <button key={key} className={`filter-btn ${filter === key ? "active" : ""}`}
                    data-testid={`portfolio-filter-${key}`} onClick={() => setFilter(key)}>
              {label}
            </button>
          ))}
        </div>

        <div className="portfolio-grid" id="portfolioGrid">
          {PORTFOLIO.filter((p) => filter === "all" || p.category === filter).map((p, i) => {
            const Icon = p.icon;
            return (
              <div className="portfolio-card" data-category={p.category} key={p.title}
                   data-testid={`portfolio-card-${i}`}>
                <div className="portfolio-thumb">
                  <Icon size={48} strokeWidth={1} className="portfolio-thumb-icon" />
                </div>
                <div className="portfolio-body">
                  <div className="portfolio-category">{p.cat}</div>
                  <div className="portfolio-title">{p.title}</div>
                  <div className="portfolio-client">{p.client}</div>
                  <div className="portfolio-desc">{p.desc}</div>
                  <div className="portfolio-tags">
                    {p.tags.map((t) => <span key={t} className="portfolio-tag">{t}</span>)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
